import { ReactNode, useEffect, useRef } from 'react';

interface LoadMoreProps {
  children: ReactNode;
  isLoading: boolean;
  onLoadMore: () => void;
  /**
   * How far ahead of the viewport the sentinel counts as reached, in multiples of the viewport's
   * height — `0.5` asks for the next page half a screen before it is scrolled to.
   */
  scrollOffset?: number;
}

/**
 * A sentinel placed after the last item: it calls `onLoadMore` once it scrolls within
 * `scrollOffset` of the viewport, and renders `children` in its place while `isLoading`.
 *
 * Render it only while there is another page to fetch — one left mounted at the end of a finished
 * list keeps asking. The callback can fire more than once for the same page, so the caller has to
 * guard against re-entry.
 */
const LoadMore = ({ children, isLoading, onLoadMore, scrollOffset = 1 }: LoadMoreProps) => {
  const sentinel = useRef<HTMLDivElement>(null);
  const loadMore = useRef(onLoadMore);

  loadMore.current = onLoadMore;

  useEffect(() => {
    const margin = 100 * scrollOffset;
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && loadMore.current(), {
      // Percentages resolve against the observer's root, here the viewport. No margin on top, so
      // the sentinel only counts as reached on the way down the list.
      rootMargin: `0px ${margin}% ${margin}% ${margin}%`,
    });

    observer.observe(sentinel.current);

    return () => observer.disconnect();
    // Re-observing once a page has landed is what asks for the next one when the sentinel never
    // left the margin: an observer that is already intersecting reports nothing further. A margin
    // this large is exactly the case where that happens.
  }, [isLoading, scrollOffset]);

  return <div ref={sentinel}>{isLoading && children}</div>;
};

export default LoadMore;
